import { useState } from "react";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { formatPersistentEditValue } from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import { useDismissAiScriptCellOverride } from "@/payroll/ai-script/query";
import type { IAiSpreadsheetCellOverride } from "@/payroll/ai-script/types";
import { InlineConfirmation } from "./inline-confirmation";
import { showPersistentEditMutationError } from "./shared";

interface OriginalValueEntryProps {
  override: IAiSpreadsheetCellOverride;
  businessId: string | null;
  runId: string;
  canResolve: boolean | null;
  onResolved: () => void;
  onWorkbookMutated?: () => void;
}

export const OriginalValueEntry = ({
  override,
  businessId,
  runId,
  canResolve,
  onResolved,
  onWorkbookMutated,
}: OriginalValueEntryProps) => {
  const dismiss = useDismissAiScriptCellOverride(businessId);
  const [isConfirming, setIsConfirming] = useState(false);

  const confirmRevert = () =>
    dismiss.mutate(
      { runId, overrideId: override.id },
      {
        onSuccess: () => {
          setIsConfirming(false);
          onWorkbookMutated?.();
          onResolved();
        },
        onError: (error) => {
          setIsConfirming(false);
          if (
            showPersistentEditMutationError("Couldn't discard this edit", error)
          ) {
            onResolved();
          }
        },
      },
    );

  return (
    <Stack
      gap="xs"
      fullWidth
      align="stretch"
      className="rounded-2xl border border-dashed border-stroke-tertiary p-4"
    >
      <Stack
        direction="row"
        gap="sm"
        align="center"
        justify="between"
        fullWidth
      >
        <Stack gap="none" className="min-w-0 flex-1">
          <Typography
            variant="bodyW1"
            className="min-w-0 truncate font-semibold"
          >
            {formatPersistentEditValue(override.previous_value)}
          </Typography>
          <Typography variant="captionW1" textColor="tertiary">
            Generated by the payroll logic
          </Typography>
        </Stack>
        {canResolve && !isConfirming ? (
          <Button
            variant="tertiary"
            size="xs"
            className="shrink-0"
            loading={dismiss.isPending}
            disabled={dismiss.isPending}
            onClick={() => setIsConfirming(true)}
          >
            Revert to this
          </Button>
        ) : (
          <Badge variant="gray" size="sm" className="shrink-0">
            Original
          </Badge>
        )}
      </Stack>
      {isConfirming ? (
        <InlineConfirmation
          message={
            <>
              Go back to the script&apos;s value? This removes your edit, so the
              cell follows the payroll logic again on every run.
            </>
          }
          confirmLabel="Revert to original"
          isPending={dismiss.isPending}
          onCancel={() => setIsConfirming(false)}
          onConfirm={confirmRevert}
        />
      ) : null}
    </Stack>
  );
};
